import React, { useState, useEffect, useRef } from 'react';
import SweetAlert from 'sweetalert2';
import { format, formatDistanceStrict } from 'date-fns';
import { frCA } from 'date-fns/locale';
import { add } from 'date-fns';
import axios from 'axios';
import { getFunctionUrl } from '../../../utils/apiHost';
import TicketResultCard from './TicketResultCard';
import { findTicketWithPaymentApi } from './ticketLookupApi';
import { getTicketImages } from '../../../utils/ticketImages';

const getCheckoutSessionUrl = () => getFunctionUrl('create-checkout-session');

export const isTrustedStripeCheckoutUrl = (value) => {
    try {
        const url = new URL(value);
        return url.protocol === 'https:' && url.hostname === 'checkout.stripe.com';
    } catch {
        return false;
    }
};

const payCard = (props) => {
    const [showCard, setShowCard] = useState(false);
    const [carBrand, setCardBrand] = useState('');
    const [carModel, setCardModel] = useState('');
    const [carPlate, setCarPlate] = useState('');

    const [ticketImages, setTicketImages] = useState([]);
    const [ticketID, setTicketid] = useState('');
    const [ticketAddress, setTicketAddress] = useState('');
    const [ticketPrice, setTicketPrice] = useState('');
    const [ticketPriceToPay, setTicketPriceToPay] = useState('');
    const [ticketPrice7Days, setTicketPrice7Days] = useState('');
    const [ticketPrice30Days, setTicketPrice30Days] = useState('');

    const [ticketDate, setTicketDate] = useState('');
    const [ticketDateText, setTicketDateText] = useState('');
    const [ticketDateText7Days, setTicketDateText7Days] = useState('');
    const [ticketDateText30Days, setTicketDateText30Days] = useState('');

    const [ticketDateUp, setTicketDateUp] = useState('');
    const [ticketTime, setTicketTime] = useState('');

    const [ticketDaysPassed, setTicketDayPassed] = useState(0);

    const [firstRun, setFirstRun] = useState(true);
    const [isPaying, setIsPaying] = useState(false);
    const [checkoutAvailable, setCheckoutAvailable] = useState(false);
    const [paymentProof, setPaymentProof] = useState('');
    const latestLookupRef = useRef(0);

    useEffect(() => {
        if (props.searchTicketID !== '' && props.turnstileToken) {
            const lookupId = latestLookupRef.current + 1;
            latestLookupRef.current = lookupId;
            setPaymentProof('');
            setShowCard(false);
            getTicketByID(props.searchTicketID, lookupId);

            return () => {
                if (latestLookupRef.current === lookupId) {
                    latestLookupRef.current += 1;
                }
            };
        }

        return undefined;
    }, [props.searchTicketID, props.turnstileToken]);

    const isLatestLookup = (lookupId) => latestLookupRef.current === lookupId;

    const setSearchLoading = (status) => {
        if (typeof props.searching === 'function') {
            props.searching(status);
        }
    };

    const getValidDate = (value) => {
        if (!value) {
            return null;
        }

        const parsedDate = new Date(value);
        return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
    };

    const formatTicketDate = (value, dateFormat) => {
        const parsedDate = getValidDate(value);

        if (!parsedDate) {
            return value || '';
        }

        return format(parsedDate, dateFormat, { locale: frCA });
    };

    const getTicketDaysPassed = (foundTicket, lookupData) => {
        if (Number.isFinite(lookupData?.daysElapsed)) {
            return lookupData.daysElapsed;
        }

        const ticketDateObject = getValidDate(foundTicket.date);

        if (!ticketDateObject) {
            return 0;
        }

        let currentDate = new Date();
        currentDate = currentDate.setHours(0, 0, 0, 0);
        let daysPassed = formatDistanceStrict(currentDate, ticketDateObject);

        if (daysPassed.includes('month') || daysPassed.includes('year')) {
            return 31;
        }

        if (daysPassed.includes('hours')) {
            return 0;
        }

        return parseInt(daysPassed.replace('days'), 10);
    };

    const getTicketPriceToPay = (foundTicket, daysPassed, lookupData) => {
        if (Number.isInteger(lookupData?.amountCents)) {
            return (lookupData.amountCents / 100).toFixed(2);
        }

        if (lookupData?.priceTier && foundTicket[lookupData.priceTier]) {
            return foundTicket[lookupData.priceTier];
        }

        if (daysPassed > 30) {
            return foundTicket.price30days;
        }

        if (daysPassed > 7) {
            return foundTicket.price7days;
        }

        return foundTicket.price;
    };

    const hydrateTicketCard = (foundTicket, searchedTicketID, lookupData = {}) => {
        if (foundTicket.status === 'Inactif') {
            setSearchLoading(false);
            setShowCard(false);
            SweetAlert.fire({ title: 'Avis annulé', text: 'Aucune action nécessaire', icon: 'success' }).then(function () {
                window.location.href = '/recherche';
            });
            return null;
        }

        if (foundTicket.payment?.paid) {
            setSearchLoading(false);
            setShowCard(false);
            SweetAlert.fire({ title: 'Avis complété', text: 'Aucune action nécessaire', icon: 'success' }).then(function () {
                window.location.href = '/recherche';
            });
            return null;
        }

        setSearchLoading(false);
        setShowCard(true);
        setFirstRun(false);
        setTicketid(foundTicket.ticketid);
        setTicketAddress(foundTicket.address);
        setCardBrand(foundTicket.carbrand);
        setCardModel(foundTicket.carmodel);
        setCarPlate(foundTicket.carplate);
        setTicketDate(foundTicket.date);
        setTicketTime(foundTicket.time);
        setTicketImages(getTicketImages(foundTicket));
        setTicketPrice(foundTicket.price);
        setTicketPrice7Days(foundTicket.price7days);
        setTicketPrice30Days(foundTicket.price30days);

        const ticketDateObject = getValidDate(foundTicket.date);
        const ticketDateSevenDays = ticketDateObject ? add(ticketDateObject, { days: 7 }) : null;
        const ticketDateThirtyDays = ticketDateObject ? add(ticketDateObject, { days: 30 }) : null;
        setTicketDateText(formatTicketDate(ticketDateObject || foundTicket.date, 'EEEE, d MMMM yyyy'));
        setTicketDateText7Days(formatTicketDate(ticketDateSevenDays, 'EEEE, d MMMM yyyy'));
        setTicketDateText30Days(formatTicketDate(ticketDateThirtyDays, 'EEEE, d MMMM yyyy'));

        const daysPassed = getTicketDaysPassed(foundTicket, lookupData);
        setTicketDayPassed(daysPassed);

        foundTicket.toPayPrice = getTicketPriceToPay(foundTicket, daysPassed, lookupData);
        setTicketPriceToPay(foundTicket.toPayPrice);

        if (props.payPage) {
            props.sendCardData(foundTicket);
        }

        if (typeof props.onTicketFound === 'function') {
            props.onTicketFound(foundTicket.ticketid || searchedTicketID);
        }

        return foundTicket;
    };

    const getTicketByID = async (pSearchID, lookupId = latestLookupRef.current) => {
        setSearchLoading(true);

        try {
            const paymentApiLookup = await findTicketWithPaymentApi(pSearchID, {
                turnstileToken: props.turnstileToken,
            });

            if (!isLatestLookup(lookupId)) {
                return null;
            }

            if (paymentApiLookup.unavailable) {
                throw new Error('Ticket lookup API unavailable');
            }

            const paymentAccess = paymentApiLookup.paymentAccess;

            if (paymentAccess) {
                setSearchLoading(false);
                setShowCard(false);

                if (!paymentAccess.payable) {
                    setCheckoutAvailable(false);
                    setPaymentProof('');
                    const alreadyPaid = paymentAccess.nonPayableReason === 'already_paid';
                    const verificationRequired = paymentAccess.nonPayableReason === 'verification_required';
                    SweetAlert.fire({
                        title: alreadyPaid ? 'Cet avis est déjà payé' : verificationRequired ? 'Avis retrouvé' : 'Avis non payable',
                        text: alreadyPaid
                            ? 'Aucun autre paiement n’est requis pour cet avis.'
                            : verificationRequired
                                ? 'Cet avis nécessite une vérification avant le paiement. Veuillez contacter Max Park en indiquant son numéro.'
                                : 'Aucun paiement en ligne ne peut être effectué pour cet avis.',
                        icon: 'info'
                    });
                    return null;
                }

                if (
                    paymentAccess.currency !== 'CAD' ||
                    !Number.isInteger(paymentAccess.amountCents) ||
                    paymentAccess.amountCents <= 0 ||
                    typeof paymentAccess.checkoutAvailable !== 'boolean' ||
                    (paymentAccess.checkoutAvailable && !paymentAccess.paymentProof)
                ) {
                    throw new Error('Public payment access response is incomplete');
                }

                setTicketid(paymentAccess.ticketNumber || pSearchID);
                setTicketPriceToPay((paymentAccess.amountCents / 100).toFixed(2));
                setCheckoutAvailable(paymentAccess.checkoutAvailable);
                setPaymentProof(paymentAccess.checkoutAvailable ? paymentAccess.paymentProof : '');
                setTicketAddress('');
                setCardBrand('');
                setCardModel('');
                setCarPlate('');
                setTicketImages(getTicketImages(paymentAccess));
                setShowCard(true);
                setFirstRun(false);
                if (typeof props.onTicketFound === 'function') {
                    props.onTicketFound(paymentAccess.ticketNumber || pSearchID);
                }
                return paymentAccess;
            }

            if (props.payPage) {
                setSearchLoading(false);
                window.location.href = '/recherche';
                return null;
            }

            setSearchLoading(false);
            setShowCard(false);
            setCheckoutAvailable(false);
            SweetAlert.fire({ title: 'Aucun avis trouvé', text: ' Veuillez vérifier le numéro de référence : ' + pSearchID, icon: 'warning' });
            return null;
        } catch (error) {
            if (!isLatestLookup(lookupId)) {
                return null;
            }

            console.error('Unable to fetch ticket data', {
                status: Number.isInteger(error?.response?.status) ? error.response.status : 'unknown',
            });
            setSearchLoading(false);
            setShowCard(false);
            setCheckoutAvailable(false);
            SweetAlert.fire({
                title: 'Recherche indisponible',
                text: "Une erreur est survenue pendant la recherche de l'avis. Veuillez réessayer.",
                icon: 'error'
            });
            return null;
        }
    };

    let paymodalFooter;

    const payerAvis = async (e) => {
        e.preventDefault();

        if (isPaying) {
            return;
        }

        if (!checkoutAvailable) {
            SweetAlert.fire({
                title: 'Paiement indisponible',
                text: "L'avis a été trouvé, mais le paiement en ligne est temporairement indisponible.",
                icon: 'info'
            });
            return;
        }

        if (!ticketID || !ticketPriceToPay || !paymentProof) {
            SweetAlert.fire({
                title: 'Paiement indisponible',
                text: "Les informations de paiement de cet avis sont incomplètes. Veuillez relancer la recherche.",
                icon: 'warning'
            });
            return;
        }

        setIsPaying(true);

        try {
            const response = await axios.post(
                getCheckoutSessionUrl(),
                {
                    id: ticketID,
                    paymentProof,
                },
                {
                    headers: {
                        'Content-Type': 'application/json'
                    }
                }
            );

            const data = response.data || {};

            if (data.status === 'ok' && isTrustedStripeCheckoutUrl(data.url)) {
                window.location.assign(data.url);
                return;
            }

            throw new Error(data.message || 'Unable to create checkout session');
        } catch (error) {
            console.error('Checkout request failed', {
                status: Number.isInteger(error?.response?.status) ? error.response.status : 'unknown',
            });
            SweetAlert.fire({
                title: 'Paiement indisponible',
                text: "La passerelle de paiement n'a pas pu être ouverte. Veuillez réessayer dans quelques instants.",
                icon: 'error'
            });
            setIsPaying(false);
        }
    };

    if (!props.payPage) {
        paymodalFooter = <form onSubmit={payerAvis}><button type="submit" disabled={isPaying || !checkoutAvailable || !paymentProof} to={`${process.env.PUBLIC_URL}/payer/` + ticketID + ticketPriceToPay + ticketID} className="btn btn-primary btn-maxpark btn-lg w-100 p-t-15 p-b-15" style={{ marginTop: 20 }} >{!checkoutAvailable ? 'Paiement en ligne temporairement indisponible' : (isPaying ? 'Redirection...' : 'Payer cet avis')}</button>
        </form>;
    } else {

        if (ticketDaysPassed > 7) {
            paymodalFooter = <p style={{ marginTop: 20 }}>Des frais additionnels ont été ajoutés car la date de paiement est échouée.</p>;
        } else {
            paymodalFooter = <p style={{ marginTop: 20 }} >Effectuez un paiement maintenant pour éviter les frais additionnels à partir du : {ticketDateText7Days} </p>;
        }
    }

    return (
        <TicketResultCard
            publicPaymentSummary={true}
            firstRun={firstRun}
            showCard={showCard}
            ticketID={ticketID}
            ticketImages={ticketImages}
            carBrand={carBrand}
            carModel={carModel}
            carPlate={carPlate}
            ticketDateText={ticketDateText}
            ticketTime={ticketTime}
            ticketAddress={ticketAddress}
            ticketPriceToPay={ticketPriceToPay}
            ticketPrice7Days={ticketPrice7Days}
            ticketPrice30Days={ticketPrice30Days}
            ticketDateText7Days={ticketDateText7Days}
            ticketDateText30Days={ticketDateText30Days}
            ticketDaysPassed={ticketDaysPassed}
            paymodalFooter={paymodalFooter}
        />
    );
};

export default payCard;
